import { useMutation, useQuery } from '@apollo/client';
import type { Account } from '@interfaces/account';
import { getAccountName } from '@interfaces/account';
import type { WorkflowCompletion } from '@interfaces/workflow';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { usePermission } from '@providers/userContextProvider';
import { getEffectiveObjectId } from '@utils/getEffectiveObjectId';
import { useHoldingFilter } from '@utils/useHoldingFilter';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { DEFAULT_RESULT_CURRENCY, REDEEM_MODES } from './calculator.constants';
import {
  FETCH_CALCULATOR_ACCOUNT,
  TRIGGER_CALCULATOR_INTEGRATION,
} from './calculator.queries';
import type { FundLineForm, RedeemMode } from './calculator.types';
import {
  buildCalculatorPayload,
  canRestorePersistedIntegrationResults,
  createEmptyFundLine,
  getAvailableCurrencies,
  getDefaultResultCurrency,
  mapBodyInputToFundLines,
  parseCalculatorBody,
  parseIntegrationResult,
  resolveRedeemMode,
  validateCalculatorForm,
} from './calculator.utils';
import { useFundLines } from './useFundLines';

type FetchCalculatorAccountResponse = {
  fetchAccount?: {
    account?: Account;
  };
};

export const useCalculator = ({
  subStepId,
  stepLoading,
  workflowCompletion,
}: {
  subStepId: string;
  stepLoading?: boolean;
  workflowCompletion: WorkflowCompletion;
}) => {
  const { t } = useTranslation('workflowCompletions');
  const { setWorkflowData, workflowData } = useContext(WorkflowContext);
  const { accountId: routeAccountId } = useParams<{ accountId?: string }>();
  const canReadAccountNumber = usePermission('read:account_number');

  const [selectedAccountId, setSelectedAccountIdState] = useState('');
  const [redeemMode, setRedeemModeState] = useState<RedeemMode | ''>('');
  const [resultCurrency, setResultCurrencyState] = useState(
    DEFAULT_RESULT_CURRENCY,
  );
  const [isCalculating, setIsCalculating] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [isIntegrationStale, setIsIntegrationStale] = useState(false);
  const initializedAccountId = useRef<string>();
  const hasHydratedBody = useRef(false);

  const markIntegrationStale = useCallback(() => {
    setIsIntegrationStale(true);
    setMutationError(null);
  }, []);

  const {
    addFundLine,
    fundLines,
    removeFundLine,
    replaceFundLines,
    updateFundLine,
  } = useFundLines(markIntegrationStale);

  const resolvedAccountId = routeAccountId || selectedAccountId;
  const workflowUserId = getEffectiveObjectId(workflowCompletion);
  const showAccountSelect = !routeAccountId;

  const { data, loading: accountLoading } =
    useQuery<FetchCalculatorAccountResponse>(
      FETCH_CALCULATOR_ACCOUNT({ canReadAccountNumber }),
      {
        variables: { accountId: resolvedAccountId },
        skip: !resolvedAccountId,
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: true,
      },
    );

  const account = data?.fetchAccount?.account;
  const accountId = account?.id;
  const accountType = account?.type;
  const accountName = getAccountName(
    account,
    !!account?.custodianAccountNumber,
  );
  const rawHoldings = account?.statistics?.holdings ?? [];
  const holdings = useHoldingFilter(rawHoldings, { excludeZeroValue: true });
  const availableCurrencies = useMemo(
    () => getAvailableCurrencies(account?.statistics),
    [account?.statistics],
  );
  const showMultipleFundOption = holdings.length > 1;

  const integrationResult = isIntegrationStale
    ? undefined
    : parseIntegrationResult(workflowData);

  const applyFormState = useCallback(
    ({
      nextFundLines,
      nextRedeemMode,
      nextResultCurrency,
      shouldMarkIntegrationStale,
    }: {
      nextFundLines: FundLineForm[];
      nextRedeemMode: RedeemMode | '';
      nextResultCurrency: string;
      shouldMarkIntegrationStale: boolean;
    }) => {
      replaceFundLines(nextFundLines);
      setRedeemModeState(nextRedeemMode);
      setResultCurrencyState(nextResultCurrency);
      setIsIntegrationStale(shouldMarkIntegrationStale);
      setMutationError(null);
    },
    [replaceFundLines],
  );

  const persistedBody = useMemo(
    () => parseCalculatorBody(workflowData.body),
    [workflowData.body],
  );

  useEffect(() => {
    if (accountLoading) return;

    if (showAccountSelect && persistedBody?.accountId && !selectedAccountId) {
      setSelectedAccountIdState(persistedBody.accountId);
    }
  }, [accountLoading, persistedBody, selectedAccountId, showAccountSelect]);

  useEffect(() => {
    if (accountLoading) return;
    if (!accountId) return;

    const isNewAccount = initializedAccountId.current !== accountId;

    if (isNewAccount) {
      initializedAccountId.current = accountId;
      hasHydratedBody.current = false;
    }

    if (hasHydratedBody.current) return;

    if (persistedBody?.accountId === accountId) {
      hasHydratedBody.current = true;
      const hydratedRedeemMode = resolveRedeemMode(
        persistedBody.redeemMode,
        holdings.length,
      );
      const canRestoreResults = canRestorePersistedIntegrationResults({
        redeemMode: hydratedRedeemMode,
        input: persistedBody.input,
        holdings,
      });

      applyFormState({
        nextFundLines:
          hydratedRedeemMode === REDEEM_MODES.ENTIRE
            ? [createEmptyFundLine()]
            : mapBodyInputToFundLines(persistedBody.input, holdings),
        nextRedeemMode: hydratedRedeemMode,
        nextResultCurrency: persistedBody.resultCurrency,
        shouldMarkIntegrationStale: !canRestoreResults,
      });
      return;
    }

    if (!isNewAccount) return;

    applyFormState({
      nextFundLines: [createEmptyFundLine()],
      nextRedeemMode: '',
      nextResultCurrency: getDefaultResultCurrency(availableCurrencies),
      shouldMarkIntegrationStale: true,
    });
  }, [
    accountLoading,
    accountId,
    applyFormState,
    availableCurrencies,
    holdings,
    persistedBody,
  ]);

  useEffect(() => {
    if (!accountId || accountLoading || !redeemMode) return;
    const nextRedeemMode = resolveRedeemMode(redeemMode, holdings.length);
    if (nextRedeemMode === redeemMode) return;
    setRedeemModeState(nextRedeemMode);
    markIntegrationStale();
  }, [
    accountId,
    accountLoading,
    holdings.length,
    markIntegrationStale,
    redeemMode,
  ]);

  const setRedeemMode = (nextRedeemMode: RedeemMode) => {
    setRedeemModeState(nextRedeemMode);
    if (nextRedeemMode === REDEEM_MODES.SINGLE && fundLines.length > 1) {
      replaceFundLines(fundLines.slice(0, 1));
    }
    markIntegrationStale();
  };

  const setResultCurrency = (nextResultCurrency: string) => {
    setResultCurrencyState(nextResultCurrency);
    markIntegrationStale();
  };

  const setSelectedAccountId = (nextAccountId: string) => {
    setSelectedAccountIdState(nextAccountId);
    markIntegrationStale();
  };

  const isValid = validateCalculatorForm({
    accountId,
    redeemMode,
    fundLines,
    holdings,
  });

  const [triggerIntegration] = useMutation(TRIGGER_CALCULATOR_INTEGRATION);

  const handleCalculate = async () => {
    if (!accountId || !isValid || !redeemMode) return;

    const payload = buildCalculatorPayload({
      accountId,
      resultCurrency,
      redeemMode,
      fundLines,
      holdings,
    });

    setIsCalculating(true);
    setMutationError(null);

    try {
      await setWorkflowData((previous) => ({
        ...previous,
        body: payload,
      }));

      const result = await triggerIntegration({
        variables: {
          input: {
            workflowCompletionId: workflowCompletion.id,
            subStepId,
            persistResponseToContext: true,
          },
        },
      });

      const context = result.data?.triggerSynchronousIntegration
        ?.workflowCompletion?.context as Record<string, unknown> | undefined;

      if (context) {
        await setWorkflowData(() => context);

        const parsed = parseIntegrationResult(context);
        if (!parsed || parsed.status === 'error' || !parsed.data) {
          setMutationError(parsed?.error?.message || t('calculator.error'));
          setIsIntegrationStale(false);
          return;
        }

        setIsIntegrationStale(false);
      } else {
        setMutationError(t('calculator.error'));
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('calculator.error');
      setMutationError(message);
    } finally {
      setIsCalculating(false);
    }
  };

  const handleClear = async () => {
    applyFormState({
      nextFundLines: [createEmptyFundLine()],
      nextRedeemMode: '',
      nextResultCurrency: getDefaultResultCurrency(availableCurrencies),
      shouldMarkIntegrationStale: true,
    });
    await setWorkflowData((previous) => {
      const { body: _, integrationResult: __, ...rest } = previous;
      return rest;
    });
  };

  return {
    accountLoading,
    accountName,
    accountType,
    addFundLine,
    availableCurrencies,
    fundLines,
    handleCalculate,
    handleClear,
    holdings,
    integrationResult,
    isCalculating: isCalculating || !!stepLoading,
    isValid,
    mutationError,
    redeemMode,
    removeFundLine,
    resultCurrency,
    selectedAccountId,
    setRedeemMode,
    setResultCurrency,
    setSelectedAccountId,
    showAccountSelect,
    showMultipleFundOption,
    updateFundLine,
    workflowUserId,
  };
};

export type CalculatorState = ReturnType<typeof useCalculator>;
